import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { StarFilled } from "@ant-design/icons";
import { Alert, Avatar, Badge, Button, Card, Col, Layout, Modal, Row, Segmented, Space, Typography } from "antd";
import { t } from "i18next";
import { useAuthenticatedFetch } from "@/Api/Axios";
import {
  cancelReasonInitialValues,
  featureList,
  formFieldsCancelReason,
  getPlansData,
  initialValues,
  interval,
} from "@/Assets/Mocks/CommonPricing.mock";
import CommonForm from "@/Components/Common/CommonForm";
import CommonSkeletonPage from "@/Components/Common/CommonSkeletonPage";
import { navigate } from "@/Components/Common/NavigationMenu";
import { ProfileContext } from "@/Context/ProfileContext";
import { ToastContext } from "@/Context/ToastContext";
import { isAdmin, removeBasePriceURL } from "@/Utils/Index";
import {
  AppList,
  dateWisePersent,
  dateWisePriceBanner,
  dateWisePriceObj,
} from "../../Assets/Mocks/CommonPricing.mock";
import SinglePlan from "./SinglePlan";

const { Text, Title } = Typography;
const { Content } = Layout;

export default function Pricing({ config, onAcceptPlan, hasBillingButton, title }) {
  const [formValues, setFormValues] = useState(initialValues);
  const [cancelReasonValues, seCancelReasonValues] = useState(cancelReasonInitialValues);
  const [isStatusActive, setStatusActive] = useState(true);
  const { profileData, updateProfileData } = useContext(ProfileContext);
  const { showToast } = useContext(ToastContext);
  const [selectedPlan, setSelectedPlan] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [urlPromoCode, setUrlPromoCode] = useState(false);
  const [pricingData, setPricingData] = useState(getPlansData());
  const [syncPlanButton, setSyncPlanButton] = useState(false);
  const [isFirstButtonActive, setIsFirstButtonActive] = useState(false);
  const [isReasonPopup, setIsReasonPopup] = useState(false);
  const [showDateWisePrice, setShowDateWisePrice] = useState(false);
  const [userCreated, setUserCreated] = useState(new Date());
  const [countDownString, setCountDownString] = useState("");
  const [minuteTrialText, setMinuteTrialText] = useState();
  const [trialDays, setTrialDays] = useState();
  const fetch = useAuthenticatedFetch();
  const location = useLocation();
  const navigateTo = navigate();
  const formRef = useRef();
  const urlParams = new URLSearchParams(location.search);
  const reviewLink = `${process.env.SHOPIFY_STORE_APP_URL}#modal-show=ReviewListingModal`;
  const plansData = getPlansData();

  // Moved the repeated logic into a separate function
  const getUrlParam = (param) => urlParams.get(param);

  const closeReasonPopup = useCallback(() => {
    setIsReasonPopup(false);
  }, []);

  const cancelPlan = useCallback(async () => {
    setIsReasonPopup(true);
  }, [selectedPlan, isReasonPopup]);

  const submitForm = useCallback((e) => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  const cancelReccuringPlan = useCallback(
    async (value) => {
      if (value.cancelReason.reason === "") {
        showToast(t(`pricing.Please Select One Option`));
      } else {
        setIsReasonPopup(false);
        let trial_days = profileData?.trial_days || profileData?.trial_days == 0 ? trialDays : 7;
        let data = {
          plan: selectedPlan,
          trial_days: trial_days,
          cancelReason: {
            reason: value?.cancelReason?.reason,
            value: value?.cancelReason?.value,
          },
        };
        const res = await fetch.post("plan/cancel", JSON.stringify(data));
        showToast(t(`pricing.Plan cancelled successfully`));
        updateProfileData(res?.data);
        fetchPlanData();
        setStatusActive(true);
      }
    },
    [selectedPlan, isReasonPopup, profileData, trialDays]
  );

  const getTrialDays = () => {
    const trialDays = profileData?.trial_days;
    const trialStart = new Date(profileData?.trial_start || new Date()); // Trial start date
    const now = new Date();
    const trialEnd = new Date(trialStart.getTime() + trialDays * 24 * 60 * 60 * 1000);
    const diffMs = trialEnd - now;
    const remainingDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return profileData?.recurringPlanId?.includes(100) ? 0 : remainingDays > 0 ? remainingDays : 0;
  };

  const upgradePlan = useCallback(
    async (plan) => {
      const { shopUrl, email } = profileData;
      const storeUrl = shopUrl.split(".myshopify.com")[0];
      const { discounts, features, ...rest } = plan;
      const planPrice = plan?.monthlyPrice ? plan.monthlyPrice : plan.price;
      const originalPrice = plan?.price;

      const newPlan = {
        ...rest,
        trial_days: profileData?.trial_days || profileData?.trial_days == 0 ? trialDays : plan?.trial?.days || 0,
        return_url: `https://admin.shopify.com/store/${storeUrl}/apps/${process.env.SHOPIFY_APP_URL_FOR_PRICING}/pricing?finalPrice=${plan.finalPrice}&&code=${plan["discountValue"]}&&discountRate=${plan["discountLabel"]}&&interval=${plan.discountObject?.interval}&&id=${plan.id}&&planPrice=${planPrice}&&originalPrice=${originalPrice}`,
        test: email?.includes("webrexstudio.com"),
      };

      const response = await fetch.post("plan", newPlan);
      const type = plan.is_recurring ? "appSubscriptionCreate" : "appPurchaseOneTimeCreate";
      const confirmationUrl = response.data?.[type].confirmationUrl;

      if (onAcceptPlan) onAcceptPlan();

      if (confirmationUrl) {
        const redirectUrl = confirmationUrl.includes("admin.shopify.com")
          ? removeBasePriceURL(confirmationUrl)
          : confirmationUrl;

        updateProfileData({ ...profileData, planAttemptTime: new Date() });
        navigateTo(
          redirectUrl,
          confirmationUrl.includes("admin.shopify.com") ? { replace: false, target: "host" } : undefined
        );
      }
    },
    [profileData, trialDays]
  );

  const checkPromoCodeValidity = (plan, promoCodes, price) => {
    const { initialDiscountObject, initialDiscountPrice, discountPercent } = plan;
    plan.discountLabel = discountPercent ? discountPercent + "%" : null;
    plan.discountValue = null;
    plan.discountPercent = discountPercent || null;
    plan.discountObject = initialDiscountObject || null;
    let finalPrice = initialDiscountPrice || price;
    if (promoCodes) {
      let codes = promoCodes.split(",");
      for (let code of codes) {
        if (plan.discounts?.length > 0) {
          let findCode = plan.discounts.find((e) => e.code.toLowerCase() === code.toLowerCase());
          if (findCode) {
            const { type, value } = findCode;

            let discount = type === "amount" ? value : (value * price) / 100;
            finalPrice = Math.floor((price - discount) * 100) / 100;

            plan.discountLabel = `${value}${type === "amount" ? "$" : "%"}`;
            plan.discountObject = findCode;
            plan.discountValue = findCode.code;
            plan.discountPercent = value;
            setPromoCode(code);
          }
        }
      }
    }
    return finalPrice;
  };

  const submitPromocode = useCallback(
    async (plan) => {
      try {
        setStatusActive(false);
        plan.touched = true;
        const code = formRef.current.values.promocode;
        if (code) {
          plan.finalPrice = checkPromoCodeValidity(plan, code, plan.monthlyPrice ? plan.monthlyPrice : plan.price);
          formRef.current.values.promocode = "";
          setFormValues((prevFormValues) => ({
            ...prevFormValues,
            promocode: code,
          }));
          if (plan.discountLabel) {
            const data = pricingData.map((e) => {
              if (e.name === plan.name) {
                return plan;
              } else if (e.selected === true) {
                return e;
              } else {
                e.touched = false;
                e.finalPrice = checkPromoCodeValidity(e, " ", e.monthlyPrice ? e.monthlyPrice : e.price);
                return e;
              }
            });
            setPricingData(data);
            setTrialDays(getTrialDays());
          }
        } else if (plan.discountLabel) {
          plan.touched = true;
        } else {
          plan.touched = false;
          setFormValues({ initialValues });
        }
      } catch (err) {
        console.error(err);
      }
    },
    [pricingData]
  );

  const fetchPlanData = useCallback(async () => {
    const res = await fetch.get("plan");
    if (res?.data) {
      setSelectedPlan(res.data);
      if ((hasBillingButton && res.data.id === "Premium-Monthly") || res.data.id === "Pro-Monthly") {
        setIsFirstButtonActive(true);
      }
    }
  }, []);

  const activatePlan = useCallback(async () => {
    const params = [
      "finalPrice",
      "code",
      "charge_id",
      "discountRate",
      "interval",
      "id",
      "planPrice",
      "originalPrice",
    ];
    const data = params.reduce((obj, param) => {
      const value = getUrlParam(param);
      obj[param] = value === "null" ? null : value;
      return obj;
    }, {});
    pricingData.find((plan) => {
      if (data.id === plan.id) {
        data["recurring"] = plan?.is_recurring;
        data["intervalLable"] = plan?.intervalLable;
        data["name"] = plan.name;
        data["billingInterval"] = plan.billingInterval;
      }
    });
    const res = await fetch.post("plan/active", data);
    updateProfileData(res?.data);
    fetchPlanData();
  }, []);

  useEffect(() => {
    let flag = false;
    if (profileData && selectedPlan && isStatusActive) {
      const data = plansData.map((plan) => {
        if (plan.id === selectedPlan.id) {
          plan["selected"] = true;
          plan["discountValue"] = selectedPlan.code;
          plan["intervalLable"] =
            selectedPlan.intervalLable && plan.id !== "Free" ? selectedPlan.intervalLable : plan.intervalLable;
          flag = true;
          plan["finalPrice"] = selectedPlan?.discountedPrice
            ? selectedPlan.discountedPrice
            : selectedPlan.planPrice;
          plan["price"] = selectedPlan?.originalPrice ? selectedPlan.originalPrice : selectedPlan.planPrice;
          if (selectedPlan.billingInterval === "Year" && selectedPlan.intervalLable === "Month") {
            plan["monthlyPrice"] = selectedPlan.planPrice;
          } else {
            plan["monthlyPrice"] = "";
          }
          if (selectedPlan?.discountValue && selectedPlan?.discountValue !== "undefined") {
            plan["discountLabel"] = selectedPlan.discountValue;
            plan["discountPercent"] = parseInt(selectedPlan.discountValue.match(/\d+/)[0], 10);
            plan["discountObject"] = {
              name: "Promotional",
              code: selectedPlan.code,
              type: "percentage",
              value: parseInt(selectedPlan.discountValue.match(/\d+/)[0], 10),
            };
          }
          if (!selectedPlan?.discountValue && plan?.initialDiscountObject) {
            plan["discountPercent"] = plan?.initialDiscountObject?.value || 0;
            plan["discountObject"] = { ...plan?.initialDiscountObject };
          }
        } else {
          if (selectedPlan.billingInterval === "Year" && plan.billingInterval !== "Year") {
            plan["disableActiveButton"] = true;
          }
          if (showDateWisePrice) {
            plan = { ...plan, ...dateWisePriceObj(userCreated, plan) };
            if (plan.billingInterval !== "Year") plan["isPromoInputHidden"] = true;
          }
          plan["selected"] = false;
          plan["finalPrice"] = checkPromoCodeValidity(
            plan,
            promoCode,
            plan.monthlyPrice ? plan.monthlyPrice : plan.price
          );
        }
        return plan;
      });
      if (!flag) {
        let feature = featureList[selectedPlan.id] ? featureList[selectedPlan.id] : featureList["Default-feature"];
        let plan = {
          type: selectedPlan.is_recurring ? "recurring" : "onetime",
          is_recurring: selectedPlan.is_recurring,
          intervalLable: selectedPlan.intervalLable,
          interval: interval[selectedPlan.id || "Default"],
          id: selectedPlan.id,
          name: selectedPlan.planName,
          isHidden: selectedPlan.billingInterval === "Year" ? "isFirstButtonActive" : "!isFirstButtonActive",
          price: selectedPlan.originalPrice ? selectedPlan.originalPrice : selectedPlan.planPrice,
          features: [...feature],
          trial: {
            days: trialDays,
          },
          selected: true,
          finalPrice: selectedPlan?.discountedPrice ? selectedPlan.discountedPrice : selectedPlan.planPrice,
          discountLabel: selectedPlan?.discountValue,
          discountValue: selectedPlan?.code,
        };
        if (selectedPlan.billingInterval === "Year" && selectedPlan.intervalLable === "Month") {
          plan["monthlyPrice"] = selectedPlan.planPrice;
        }
        data.push(plan);
      }
      setPricingData(data);
      setTrialDays(getTrialDays());
    }
  }, [profileData, selectedPlan]);

  const handleUrlParams = useCallback(() => {
    const chargeId = getUrlParam("charge_id");
    const code = getUrlParam("promocode");

    if (code) {
      setPromoCode(code);
      setUrlPromoCode(true);
    }

    if (chargeId) {
      setSelectedPlan(false);
      activatePlan();
    } else {
      fetchPlanData();
    }
  }, []);

  const activeSyncPlan = useCallback(async () => {
    const res = await fetch.post("activeSyncPlan");
    updateProfileData(res?.data);
    fetchPlanData();
    setStatusActive(true);
  }, [selectedPlan]);

  const syncPlan = useCallback(() => {
    let adminStatus = isAdmin();
    setSyncPlanButton(adminStatus);
  }, [syncPlanButton]);

  const priceSegmentedButton = useCallback((status) => {
    setIsFirstButtonActive(status);
  }, []);

  useEffect(() => {
    handleUrlParams();
    syncPlan();
  }, []);

  function minTwoDigits(n) {
    return String(n).padStart(2, "0");
  }

  useEffect(() => {
    let interval;
    if (profileData) {
      const newDate = new Date();
      const profileDate = new Date(profileData?.discountAppliedDate || new Date());
      profileDate.setDate(profileDate.getDate() + 2);
      const countDownDate = profileDate - newDate;
      const daysDifference = countDownDate / (1000 * 60 * 60 * 24);
      if (daysDifference > 0) {
        interval = setInterval(() => {
          const newDate = new Date();
          const countDownDate = profileDate - newDate;
          const hoursDifference = countDownDate / (1000 * 60 * 60);
          if (hoursDifference <= 48 && hoursDifference > 0) {
            const hours = minTwoDigits(Math.floor(hoursDifference));
            const minutes = minTwoDigits(Math.floor((countDownDate / 1000 / 60) % 60));
            const seconds = minTwoDigits(Math.floor((countDownDate / 1000) % 60));
            setCountDownString(
              `${t(`pricing.This offer expire in`)} ${hours}:${minutes}:${seconds} ${t(`pricing.Hurry Up!`)}`
            );
          } else {
            fetchPlanData();
            setShowDateWisePrice(false);
            clearInterval(interval);
          }
        }, 1000);
      }
    }

    return () => clearInterval(interval);
  }, [profileData]);

  useEffect(() => {
    let interval;

    if (profileData) {
      const trialDays = profileData?.trial_days;
      const trialStart = new Date(profileData?.trial_start || new Date()); // Trial start date
      interval = setInterval(() => {
        const now = new Date();
        const trialEnd = new Date(trialStart.getTime() + trialDays * 24 * 60 * 60 * 1000);
        const diffMs = trialEnd - now;
        if (diffMs <= 0) {
          clearInterval(interval);
          setMinuteTrialText("0 day");
          return;
        }

        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

        const twoLetterFormat = (time) => (time?.toString()?.length > 1 ? time : `0${time}`);

        setMinuteTrialText(
          `${twoLetterFormat(diffDays)} : ${twoLetterFormat(diffHours)} : ${twoLetterFormat(
            diffMinutes
          )} : ${twoLetterFormat(diffSeconds)}`
        );
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [profileData]);

  useEffect(() => {
    setUserCreated(profileData?.created_at || profileData?.created || new Date());
    if (profileData) {
      const profileDate = new Date(profileData?.discountAppliedDate || new Date());
      profileDate.setDate(profileDate.getDate() + 2);
      const countDownDate = profileDate - new Date();
      const daysDifference = countDownDate / (1000 * 60 * 60 * 24);
      if (daysDifference <= 2 && daysDifference > 0) {
        // setShowDateWisePrice(true); // comment show banner
      }
    }
  }, [profileData]);

  const filteredPlans = pricingData.filter((plan) => {
    if (config?.plans?.length > 0 && !config.plans.includes(plan.id)) {
      return false;
    } else if (typeof plan.isHidden === "string") {
      return !eval(plan.isHidden);
    } else {
      return !plan.isHidden;
    }
  });

  const renderPlan = () => (
    <Space direction="vertical" size="large" style={{ width: "100%", textAlign: "center" }}>
      <Row gutter={[16, 16]} justify="center">
        {filteredPlans.map((plan, index) => {
          const spanSize = filteredPlans.length === 1 ? 24 : filteredPlans.length === 2 ? 12 : 8;
          return (
            <Col xs={24} sm={12} md={spanSize} key={index}>
              <SinglePlan
                plan={plan}
                formRef={formRef}
                formValues={formValues}
                upgradePlan={upgradePlan}
                cancelPlan={cancelPlan}
                submitPromocode={submitPromocode}
                trialDays={trialDays}
                profileData={profileData}
                urlPromoCode={urlPromoCode}
                minuteTrialText={minuteTrialText}
              />
            </Col>
          );
        })}
      </Row>
    </Space>
  );
  if (!selectedPlan || !profileData) return <CommonSkeletonPage />;
  return (
    <>
      {profileData && profileData.recurringPlanId !== "Premium100" && profileData.recurringPlanId !== "Pro100" ? (
        <div className="pricing-plan">
          {!config?.hideHeader ? (
            <Layout style={{ minHeight: "100vh" }}>
              <Content style={{ padding: "24px" }}>
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "16px",
                    }}
                  >
                    <Title level={2} style={{ margin: 0 }}>
                      {title ? title : t(`pricing.Pricing`)}
                    </Title>
                    <Space size="middle">
                      {syncPlanButton && (
                        <Button type="primary" size="middle" onClick={activeSyncPlan}>
                          {t(`pricing.Sync Plan`)}
                        </Button>
                      )}
                      {hasBillingButton && (
                        <Segmented
                          value={isFirstButtonActive ? "monthly" : "annually"}
                          onChange={(value) => priceSegmentedButton(value === "monthly")}
                          options={[
                            {
                              label: (
                                <Space size="small">
                                  <span>{t(`pricing.Billed Monthly`)}</span>
                                  {showDateWisePrice && dateWisePersent(userCreated, true) && (
                                    <Badge color="blue">
                                      {t(`pricing.Save`)} {dateWisePersent(userCreated, true)}%
                                    </Badge>
                                  )}
                                </Space>
                              ),
                              value: "monthly",
                            },
                            {
                              label: (
                                <Space size="small">
                                  <span>{t(`pricing.Billed Annually`)}</span>
                                  {showDateWisePrice ? (
                                    <Badge color="blue">
                                      {t(`pricing.Save`)} {dateWisePersent(userCreated, false)}%
                                    </Badge>
                                  ) : (
                                    <Badge color="blue">{t(`pricing.Save`)} 30%</Badge>
                                  )}
                                </Space>
                              ),
                              value: "annually",
                            },
                          ]}
                        />
                      )}
                    </Space>
                  </div>
                  {showDateWisePrice && (
                    <Alert
                      type="info"
                      message={`${
                        dateWisePriceBanner(userCreated, isFirstButtonActive)?.bannerTitle
                      } ${countDownString}`}
                      description={dateWisePriceBanner(userCreated, isFirstButtonActive)?.bannerMsg}
                      showIcon
                    />
                  )}
                  {renderPlan()}
                </Space>
              </Content>
            </Layout>
          ) : (
            <Space direction="vertical" size="large" style={{ width: "100%", padding: "24px" }}>
              {showDateWisePrice && (
                <Alert
                  type="info"
                  message={`${
                    dateWisePriceBanner(userCreated, isFirstButtonActive)?.bannerTitle
                  } ${countDownString}`}
                  description={dateWisePriceBanner(userCreated, isFirstButtonActive)?.bannerMsg}
                  showIcon
                />
              )}
              {renderPlan()}
            </Space>
          )}
        </div>
      ) : (
        <Layout style={{ minHeight: "100vh" }}>
          <Content style={{ padding: "24px" }}>
            <div style={{ paddingTop: "40px" }}>
              <Alert
                type="info"
                message={
                  <Title level={4} style={{ margin: 0 }}>
                    {t(`pricing.Premium100Title`)}
                    <br />
                    <br />
                    {t(`pricing.Premium100ReviewLine`)}
                  </Title>
                }
                action={
                  <Space>
                    <Button href={reviewLink} target="_blank">
                      {t(`pricing.Leave Review`)}
                    </Button>
                    <Button href={process.env.SHOPIFY_STORE_APP_URL} target="_blank">
                      {t(`pricing.Check Our Current Pricing`)}
                    </Button>
                  </Space>
                }
                showIcon
              />
            </div>
          </Content>
        </Layout>
      )}
      <Modal
        open={isReasonPopup}
        onCancel={closeReasonPopup}
        onOk={submitForm}
        title={t(`pricing.We're sorry to see you go!`)}
        okText={t(`pricing.Cancel Plan`)}
        cancelText="Close"
      >
        <CommonForm
          onSubmit={cancelReccuringPlan}
          formRef={formRef}
          initialValues={cancelReasonValues}
          formFields={formFieldsCancelReason.map((value) => ({
            ...value,
            label: (
              <Title level={5} style={{ margin: 0, fontWeight: "500" }}>
                {t(`pricing.${value.label}`)}
              </Title>
            ),
            subfields: formFieldsCancelReason[0].subfields.map((value) => ({
              ...value,
              label: t(`pricing.${value.label}`),
            })),
          }))}
          isSave={false}
          noValueChanged={false}
        />
      </Modal>
      {!config && (
        <div style={{ margin: "10px 40px" }}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Title level={4} style={{ margin: 0 }}>
                Recommended Apps
              </Title>
              <Row gutter={[16, 16]}>
                {AppList.filter((i) => !i.hide).map((app, index) => (
                  <Col xs={24} sm={12} md={8} key={index}>
                    <Space size="large" align="start" style={{ width: "100%" }}>
                      <div style={{ position: "relative", overflow: "hidden" }}>
                        <Avatar src={app.image} size={64} shape="square" alt="App thumbnail" />
                        {app?.popular && (
                          <div className="recommendedApp">
                            <div style={{ marginLeft: "10px" }}>Most Popular</div>
                          </div>
                        )}
                      </div>
                      <Space direction="vertical" size="small" style={{ flex: 1 }}>
                        <Space size="small" align="center">
                          <Title level={5} style={{ margin: 0 }}>
                            {app.title}
                          </Title>
                        </Space>
                        <Space size="small" align="center">
                          <Text style={{ fontSize: "14px", color: "#666" }}>{app.starRate}</Text>
                          <StarFilled style={{ color: "#52c41a", fontSize: "16px" }} />
                          <Text style={{ fontSize: "14px", color: "#666" }}>
                            ({app.reviweCount}) • Free plan available
                          </Text>
                        </Space>
                        <Button href={app.link} target="_blank" size="small">
                          Install Now
                        </Button>
                      </Space>
                    </Space>
                  </Col>
                ))}
              </Row>
            </Space>
          </Card>
        </div>
      )}
    </>
  );
}
